import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { IconCheckbox } from '@tabler/icons-react';
import { sumBy } from 'lodash';
import React from 'react';
import { JobDto, JobUserDto, TimesheetDto } from 'src/api/FiretrolApiClient';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { formatDate, formatHours } from 'src/utils';
import {
  formatWorkWeekId,
  getWorkWeek,
  parseWorkWeekId,
} from 'src/utils/workWeek';
import { TimesheetCard } from 'src/routes/Jobs/Timesheets/components/TimesheetCard';
import { tempOptions } from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import {
  IRosterMember,
  withTimesheetUsers,
} from 'src/routes/Jobs/Timesheets/timesheetRoster';

interface TimesheetProps {
  timesheets: TimesheetDto[];
  team: JobUserDto[];
  selectedDate: Date;
  selectedJob: JobDto;
  onTimesheetChanged: (timesheet: TimesheetDto) => void;
}

export const TimesheetForm: React.FC<TimesheetProps> = ({
  timesheets,
  team,
  selectedDate,
  selectedJob,
  onTimesheetChanged,
}) => {
  const [selectedUser, setSelectedUser] = React.useState<IRosterMember | null>(
    null,
  );
  const [savedTimesheets, setSavedTimesheets] = React.useState<TimesheetDto[]>(
    [],
  );

  const roster = React.useMemo(
    () => withTimesheetUsers(team, timesheets),
    [team, timesheets],
  );

  const openTimesheet = (user: IRosterMember) => {
    setSelectedUser(user);
  };

  const closeTimesheet = () => {
    setSelectedUser(null);
  };

  const selectedWeek = React.useMemo(() => {
    const { weekNumber, year } = getWorkWeek(selectedDate);
    return tempOptions.find((o) => o.id === formatWorkWeekId(year, weekNumber));
  }, [selectedDate]);

  const onSaveOnlyComplete = (timesheet: TimesheetDto) => {
    setSavedTimesheets((prevState) => [
      ...prevState.filter((t) => t.userId !== timesheet.userId),
      timesheet,
    ]);
    onTimesheetChanged(timesheet);
    closeTimesheet();
  };

  const renderTimesheets = () => {
    return roster.map((u) => {
      const dayValue = formatDate(selectedDate, 'dddd')
        .substring(0, 3)
        .toLowerCase();
      const hasBeenUpdated = savedTimesheets.find((t) => t.userId === u.userId);
      const existing = timesheets.find((t) => t.userId === u.userId);

      const existingHasDay = timesheets.find(
        (t) => t.userId === u.userId && t.rows.find((r) => r[dayValue] !== 0),
      );
      const updateTotal = sumBy(hasBeenUpdated?.rows || [], function (o) {
        return Number(o.total);
      });

      const existingTotal = sumBy(existing?.rows || [], function (o) {
        return Number(o.total);
      });

      return (
        <Group key={u.userId} justify="flex-start" gap="xs">
          <BasicButton onClick={() => openTimesheet(u)}>
            {u.user?.fullName}
          </BasicButton>
          {hasBeenUpdated ? (
            <Text size="md">({formatHours(updateTotal)} hrs)</Text>
          ) : (
            <>
              {existing && (
                <Text size="md">({formatHours(existingTotal)} hrs)</Text>
              )}
            </>
          )}
          {(hasBeenUpdated || existingHasDay) && (
            <IconCheckbox color="green" size={22} />
          )}
        </Group>
      );
    });
  };

  return (
    <>
      <Stack gap="xs">{renderTimesheets()}</Stack>

      <BaseModal
        opened={selectedUser !== null}
        onClose={closeTimesheet}
        title={selectedUser?.user?.fullName || 'Timesheet'}
        size="xl"
        fullScreen
      >
        <Stack p="md">
          {selectedWeek && selectedUser && (
            <TimesheetCard
              selectedJob={selectedJob}
              weekId={`${parseWorkWeekId(selectedWeek.id).weekNumber}`}
              year={`${parseWorkWeekId(selectedWeek.id).year}`}
              userId={selectedUser.userId?.toString() ?? ''}
              team={team}
              onSaveOnlyComplete={onSaveOnlyComplete}
              showNextButtons={false}
            />
          )}
        </Stack>
      </BaseModal>
    </>
  );
};
